import {
  Component,
  input,
  output,
  ChangeDetectionStrategy,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { fadeInOut, scaleIn } from '../../animations/animations';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div 
      class="modal-backdrop" 
      [class.full-mobile]="fullScreenOnMobile()"
      @fadeInOut 
      role="presentation"
    >
      <div 
        #dialogRef
        class="modal-card" 
        [class.full-mobile]="fullScreenOnMobile()"
        @scaleIn
        (click)="$event.stopPropagation()" 
        (keydown)="onDialogKeydown($event)"
        [style.max-width]="maxWidth()"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        [attr.aria-labelledby]="modalId + '-title'"
      >
        <div class="modal-header">
          <div class="header-title">
            @if (icon()) {
              <div class="header-icon" aria-hidden="true">
                <i class="bi" [class]="icon()"></i>
              </div>
            }
            <h2 [id]="modalId + '-title'">{{ title() }}</h2>
          </div>
          @if (showCloseButton()) {
            <button 
              class="btn-close-modal" 
              (click)="close()" 
              aria-label="Fechar modal"
              title="Fechar"
            >
              <i class="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          }
        </div>

        <div class="modal-body" [class.no-padding]="noPadding()">
          <ng-content select="[body]"></ng-content>
        </div>

        @if (hasFooter()) {
          <div class="modal-footer">
            <ng-content select="[footer]"></ng-content>
          </div>
        }
      </div>
    </div>
  `,
  styles: `
    @use 'sass:map';
    @use '../../../styles/variables' as *;
    @use '../../../styles/mixins' as *;

    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(map.get($colors, 'gray', '900'), 0.4);
      @include flex-center;
      z-index: map.get($z-index, 'modal');
      backdrop-filter: blur(4px);
      padding: $space-4;
    }

    .modal-backdrop.full-mobile {
      @include media-breakpoint-down('sm') {
        padding: 0;
      }
    }

    .modal-card {
      width: 100%;
      background: white;
      border-radius: $radius-xl;
      @include shadow('lg');
      @include flex-column;
      max-height: calc(100vh - $space-8);
      overflow: hidden;
      border: 1px solid $border-color;
    }

    .modal-card.full-mobile {
      @include media-breakpoint-down('sm') {
        max-width: none !important;
        height: 100dvh;
        max-height: 100dvh;
        border-radius: 0;
        border: 0;
      }
    }

    .modal-header {
      @include flex(row, space-between, center, nowrap, $space-4);
      padding: $space-4 $space-6;
      background: map.get($colors, 'gray', '50');
      border-bottom: 1px solid $border-color;

      .header-title {
        @include flex(row, flex-start, center, nowrap, $space-3);
        
        .header-icon {
          @include flex-center;
          width: 36px;
          height: 36px;
          background: $color-primary-light;
          color: $color-primary;
          border-radius: $radius-md;
          font-size: 1.25rem;
        }

        h2 {
          font-size: $font-size-lg;
          font-weight: $font-weight-bold;
          color: $color-text-dark;
        }
      }

      .btn-close-modal {
        @include button-reset;
        @include flex-center;
        width: 32px;
        height: 32px;
        border-radius: $radius-md;
        color: $color-text-light;
        transition: $transition-fast;

        &:hover {
          background: map.get($colors, 'error', 'light');
          color: map.get($colors, 'error', 'base');
        }
      }
    }

    .modal-body {
      padding: $space-6;
      overflow-y: auto;
      flex: 1;

      &.no-padding { padding: 0; }
    }

    .modal-footer {
      padding: $space-4 $space-6;
      background: map.get($colors, 'gray', '50');
      border-top: 1px solid $border-color;
      @include flex(row, flex-end, center, nowrap, $space-3);

      ::ng-deep > * {
        @include flex(row, flex-end, center, nowrap, $space-3);
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInOut, scaleIn]
})
export class ModalComponent implements OnInit, OnDestroy {
  modalId = 'modal-' + Math.random().toString(36).substring(2, 9);

  open = input<boolean>(false);
  title = input<string>('');
  icon = input<string>('');
  maxWidth = input<string>('600px');
  fullScreenOnMobile = input<boolean>(false);
  noPadding = input<boolean>(false);
  showCloseButton = input<boolean>(true);
  hasFooter = input<boolean>(true);

  closeModal = output<void>();
  dialogRef = viewChild<ElementRef<HTMLElement>>('dialogRef');

  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private previouslyFocusedElement: HTMLElement | null = null;

  ngOnInit(): void {
    if (!this.isBrowser) return;
    this.previouslyFocusedElement = this.document.activeElement as HTMLElement | null;
    queueMicrotask(() => this.focusFirstElement());
  }

  ngOnDestroy(): void {
    this.restorePreviousFocus();
  }

  close(): void {
    this.closeModal.emit();
  }

  onDialogKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusable = this.getFocusableElements();
    if (!focusable.length) {
      event.preventDefault();
      this.dialogRef()?.nativeElement.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = this.document.activeElement as HTMLElement | null;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private focusFirstElement(): void {
    const focusable = this.getFocusableElements();
    if (focusable.length) {
      focusable[0].focus();
      return;
    }

    this.dialogRef()?.nativeElement.focus();
  }

  private getFocusableElements(): HTMLElement[] {
    const root = this.dialogRef()?.nativeElement;
    if (!root) return [];

    const selector = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
      element => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true',
    );
  }

  private restorePreviousFocus(): void {
    if (!this.previouslyFocusedElement) return;
    if (typeof this.previouslyFocusedElement.focus !== 'function') return;
    this.previouslyFocusedElement.focus();
    this.previouslyFocusedElement = null;
  }
}
